import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAnalysisSchemasQuery } from '../api/queries/analysisSchemas';
import { useStateLocalStorage } from '../hooks/useStateLocalStorage';
import { useErrorToast } from '../lib/useErrorToast';
import { Select } from '@convergence/components';
import { AnalysisSchemaDtoType } from '@convergence/client';

interface AnalysisSchemaSelectorProps {
  onChange: (schema: AnalysisSchemaDtoType, version: number) => void;
}

export function AnalysisSchemaSelector(props: AnalysisSchemaSelectorProps) {
  const [selectedSchemaIdAndVersion, setSelectedSchemaIdAndVersion] =
    useStateLocalStorage<{ schemaId: number; version: number } | null>(
      'schemaId+version',
      null
    );
  const analysisSchemasQuery = useAnalysisSchemasQuery();

  useErrorToast(analysisSchemasQuery.error);

  const getSelectedSchema = (id: number) => {
    return analysisSchemasQuery.data?.analysisSchemas.find((s) => s.id === id);
  };

  useEffect(() => {
    if (selectedSchemaIdAndVersion && analysisSchemasQuery.data) {
      const selectedSchema = getSelectedSchema(
        selectedSchemaIdAndVersion.schemaId
      );
      if (!selectedSchema) {
        toast.error('Selected schema not found. asdf');
        return;
      }
      props.onChange(selectedSchema, selectedSchemaIdAndVersion.version);
      return;
    } else if (
      analysisSchemasQuery.data &&
      analysisSchemasQuery.data.analysisSchemas.length > 0 &&
      selectedSchemaIdAndVersion === null
    ) {
      const schemaId = analysisSchemasQuery.data.analysisSchemas[0]?.id;
      const version =
        analysisSchemasQuery.data.analysisSchemas[0]?.versions.reduce(
          (max, v) => (v > max ? v : max),
          1
        );

      if (schemaId && version) {
        const selectedSchema = getSelectedSchema(schemaId);
        if (!selectedSchema) {
          toast.error('No analysis schemas available.');
          return;
        }
        props.onChange(selectedSchema, version);
        console.log('Auto-selecting schema:', { schemaId, version });
        setSelectedSchemaIdAndVersion({ schemaId, version });
      } else {
        toast.error('No analysis schemas available.');
      }
    }
  }, [analysisSchemasQuery.data]);

  const selectedSchema = analysisSchemasQuery.data?.analysisSchemas.find(
    (s) => s.id === selectedSchemaIdAndVersion?.schemaId
  );

  function handleSchemaChange(schemaId: number) {
    const selectedSchema = getSelectedSchema(schemaId);
    if (!selectedSchema) {
      toast.error('Selected schema not found.');
      return;
    }
    const maxVersion = getMaxVersion(selectedSchema.versions);
    setSelectedSchemaIdAndVersion({ schemaId, version: maxVersion });
    props.onChange(selectedSchema, maxVersion);
  }

  function handleVersionChange(version: number) {
    if (!selectedSchemaIdAndVersion) return;
    const selectedSchema = getSelectedSchema(
      selectedSchemaIdAndVersion.schemaId
    );
    if (!selectedSchema) {
      toast.error('Selected schema not found.');
      return;
    }
    setSelectedSchemaIdAndVersion({
      ...selectedSchemaIdAndVersion,
      version
    });
    props.onChange(selectedSchema, version);
  }

  return (
    <div className="flex gap-2 items-center">
      {selectedSchemaIdAndVersion && selectedSchema && (
        <>
          <label className="">Analysis Type:</label>
          <Select
            className="self-baseline"
            value={selectedSchemaIdAndVersion.schemaId}
            onChange={(e) => handleSchemaChange(Number(e.target.value))}
          >
            {analysisSchemasQuery.data?.analysisSchemas.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <label className="">Version:</label>
          <Select
            className="self-baseline"
            value={selectedSchemaIdAndVersion.version}
            onChange={(e) => handleVersionChange(Number(e.target.value))}
          >
            {selectedSchema?.versions.map((s) => (
              <option key={`version-${s}`} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </>
      )}
    </div>
  );
}

function getMaxVersion(versions: number[]) {
  return versions.reduce((max, v) => (v > max ? v : max), 1);
}
